import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ClipboardList, Building2, BarChart3, FileText, BadgeDollarSign, ArrowRightLeft, Plane, Map as MapIcon } from 'lucide-react';
import MortgageCalculator from '@/components/MortgageCalculator';
import RatesComparison from '@/components/RatesComparison';
import TariffCalculator from '@/components/TariffCalculator';
import ClosingAssistant from '@/components/dashboard/ClosingAssistant';
import ProceduresNavigator from '@/components/ProceduresNavigator';
import AppraisalEstimator from '@/components/AppraisalEstimator';
import UnitConverter from '@/components/UnitConverter';
import DiasporaGuide from '@/components/DiasporaGuide';
import CadastralMap from '@/components/CadastralMap';
import ROICalculator from '@/components/ROICalculator';
import ConstructionPlanner from '@/components/ConstructionPlanner';
import CommissionCalculator from '@/components/marketing/CommissionCalculator';
import CurrencyConverter from '@/components/CurrencyConverter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import OverlayGate from '@/components/OverlayGate';
import FinancialCenterLanding from '@/components/FinancialCenterLanding';
import SeoHead from '@/components/SeoHead';

const FinancialCenter = () => {
    const { user, loading: authLoading } = useAuth();
    const { subscription, hasAccess, loading: subLoading } = useSubscription();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState('mortgage');
    const [inventoryValue, setInventoryValue] = React.useState(0);

    // 1. ANONYMOUS ACCESS GUARD
    if (!authLoading && !user) {
        return <FinancialCenterLanding />;
    }

    const planId = (subscription?.plan || 'esencial').toLowerCase();
    const isFree = planId === 'esencial';

    // 2. TRIAL LOGIC (Based on User Registration Date from SubscriptionContext)
    const trialEndsAt = subscription?.trialEnd ? new Date(subscription.trialEnd).getTime() : 0;
    const trialActive = Boolean(isFree && trialEndsAt && Date.now() < trialEndsAt);
    const trialDaysLeft = trialActive ? Math.ceil((trialEndsAt - Date.now()) / (24 * 60 * 60 * 1000)) : 0;

    const isLocked = (featureKey) => {
        // Master logic: check contextual access first
        if (hasAccess(featureKey)) return false;

        // Trial logic: if free, check if 3-day trial is still active
        if (isFree && trialActive) return false;

        return true;
    };

    const getLockedMessage = (featureKey) => {
        if (isFree && !trialActive) {
            return (
                <span>
                    <strong>Tu prueba gratuita ha terminado.</strong><br />
                    Activa el Plan Premium para seguir usando esta herramienta y obtener resultados profesionales.
                </span>
            );
        }
        return "Activa el Plan Premium para desbloquear estas funciones avanzadas de análisis financiero.";
    };

    // Removed login wall - FREE users can access Financial Center
    // Premium features will be gated individually

    return (
        <div className="min-h-screen bg-white pt-24 pb-16">
            <SeoHead
                title="Herramientas Financieras Inmobiliarias | Servicios Futura"
                description="Utiliza nuestras herramientas financieras: calculadora de hipotecas, comparación de tasas bancarias, cálculo de aranceles y más."
                schema={{
                    "@context": "https://schema.org",
                    "@type": "FinancialService",
                    "name": "Centro Financiero de Servicios Futura",
                    "description": "Herramientas de cálculo hipotecario y financiero para el mercado inmobiliario de El Salvador.",
                    "serviceType": ["Mortgage Calculator", "Investment Analysis", "Tax Calculator"]
                }}
            />
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-[#1A3D5C] mb-4">
                        Centro Financiero
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Herramientas y recursos para tomar decisiones financieras informadas sobre tu próxima inversión inmobiliaria.
                    </p>
                    {/* Banner de Trial Mejorado (Solo usuarios autenticados) */}
                    {user && isFree && trialActive && (
                        <div className="sticky top-24 z-30 mt-8 p-6 bg-gradient-to-r from-[#1A3D5C] to-[#2563EB] text-white border border-blue-400/30 rounded-2xl max-w-2xl mx-auto shadow-xl animate-in fade-in slide-in-from-top-4 duration-500 ring-4 ring-blue-500/10">
                            <div className="flex items-center gap-4 text-left">
                                <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl shadow-inner border border-white/30">
                                    <span className="text-2xl animate-pulse">⭐</span>
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-center mb-1">
                                        <h4 className="font-bold text-white text-lg">Prueba PRO activa</h4>
                                        <Badge className="bg-white/20 text-white border-0">{trialDaysLeft} días restantes</Badge>
                                    </div>
                                    <p className="text-sm text-blue-50 leading-relaxed font-medium">
                                        Tienes acceso completo para probar las herramientas profesionales.
                                        Finaliza tus análisis antes de que termine tu prueba.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {user && isFree && !trialActive && (
                        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-700 px-6 py-2.5 text-sm font-bold border border-amber-200 shadow-sm">
                            🔒 Tu prueba gratuita ha terminado. Actualiza para recuperar el acceso.
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <Tabs defaultValue="mortgage" className="w-full">
                    {/* Desktop: Horizontal scroll, Mobile: Grid */}
                    <div className="mb-12">
                        <TabsList className="h-auto p-0 bg-transparent w-full grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { id: 'mortgage', label: 'Hipoteca', icon: ClipboardList, desc: 'Calcula cuotas e intereses' },
                                { id: 'tariffs', label: 'Aranceles', icon: Building2, desc: 'Costos de registro y CNR' },
                                { id: 'rates', label: 'Tasas', icon: BarChart3, desc: 'Compara bancos locales' },
                                { id: 'procedures', label: 'Trámites', icon: FileText, desc: 'Guía paso a paso' },
                                { id: 'appraisals', label: 'Avalúos', icon: BadgeDollarSign, desc: 'Estimación de mercado' },
                                { id: 'map', label: 'Mapa Catastral', icon: MapIcon, desc: 'Consulta de ubicaciones' },
                                { id: 'diaspora', label: 'H. Lejanos', icon: Plane, desc: 'Crédito para salvadoreños' },
                                { id: 'conversions', label: 'Conversiones', icon: ArrowRightLeft, desc: 'Metros, Varas y Manzanas' },
                                { id: 'roi', label: 'Calc. ROI', icon: BarChart3, desc: 'Retorno de inversión' },
                                { id: 'construction', label: 'Construcción', icon: Building2, desc: 'Presupuesto de materiales' },
                                { id: 'commission', label: 'Comisiones', icon: BadgeDollarSign, desc: 'Cálculo de honorarios' }
                            ].map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <TabsTrigger
                                        key={tab.id}
                                        value={tab.id}
                                        title={tab.desc}
                                        className="
                                            group flex flex-col items-center justify-center gap-3 p-4 h-32 rounded-xl border border-slate-200 bg-white transition-all
                                            hover:border-blue-300 hover:shadow-md
                                            data-[state=active]:bg-[#1A3D5C] data-[state=active]:text-white data-[state=active]:border-transparent data-[state=active]:shadow-lg
                                            data-[state=active]:scale-[1.02] relative
                                        "
                                    >
                                        <div className="p-3 rounded-full bg-slate-50 group-data-[state=active]:bg-white/20 transition-colors">
                                            <Icon className="w-6 h-6 text-[#1A3D5C] group-data-[state=active]:text-white" />
                                        </div>
                                        <span className="font-semibold text-sm md:text-base">{tab.label}</span>
                                        {/* Simple Tooltip on hover */}
                                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                                            {tab.desc}
                                        </div>
                                    </TabsTrigger>
                                );
                            })}
                        </TabsList>
                    </div>


                    {/* Content */}
                    <TabsContent value="mortgage" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.mortgage')}
                            title="Herramienta Profesional"
                            message={getLockedMessage('financial.mortgage')}
                            primaryCta="Ver Planes y Precios"
                            onPrimary={() => navigate('/subscription')}
                            onSecondary={() => navigate('/subscription')}
                        >
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-gray-800">Calculadora de Hipoteca Profesional</h2>
                                <p className="text-gray-600">Estima tu pago mensual, intereses y costos totales con nuestra herramienta avanzada.</p>
                            </div>
                            <MortgageCalculator />
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="tariffs" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.tariffs')}
                            title="Herramienta Profesional"
                            message={getLockedMessage('financial.tariffs')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-4xl mx-auto">
                                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Calculadora de Aranceles y Registros</h2>
                                <TariffCalculator />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="rates" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.rates')}
                            title="Herramienta Profesional"
                            message={getLockedMessage('financial.rates')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-4xl mx-auto">
                                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Comparativo de Tasas Bancarias</h2>
                                <RatesComparison />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="procedures" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.procedures')}
                            title="Biblioteca de Conocimiento"
                            message={getLockedMessage('financial.procedures')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-4xl mx-auto">
                                <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Guía Interactiva de Trámites</h2>
                                <ProceduresNavigator />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="appraisals" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.appraisals')}
                            title="Estimación Referencial"
                            message={getLockedMessage('financial.appraisals')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-5xl mx-auto">
                                <div className="text-center mb-8">
                                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Estimador de Valor de Propiedad</h2>
                                    <p className="text-gray-600">Obtén una referencia de valor basada en métodos de mercado salvadoreños.</p>
                                </div>
                                <AppraisalEstimator />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="map" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.map')}
                            title="Mapa Inteligente"
                            message={getLockedMessage('financial.map')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-5xl mx-auto">
                                <CadastralMap />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="diaspora" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.diaspora')}
                            title="Guía Especializada"
                            message={getLockedMessage('financial.diaspora')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-4xl mx-auto">
                                <DiasporaGuide />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="conversions" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.conversions')}
                            title="Herramienta de Medición y Cambio"
                            message={getLockedMessage('financial.conversions')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-4xl mx-auto space-y-12">
                                <div className="text-center">
                                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Calculadoras de Conversión</h2>
                                    <p className="text-gray-600">Divisas en tiempo real y unidades de medida locales (Varas, Manzanas).</p>
                                </div>

                                <CurrencyConverter />

                                <div className="pt-8 border-t border-slate-100">
                                    <h3 className="text-xl font-bold text-center mb-6">Equivalencias de Terreno</h3>
                                    <UnitConverter />
                                </div>
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="roi" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.roi')}
                            title="Análisis de Inversión"
                            message={getLockedMessage('financial.roi')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-6xl mx-auto">
                                <ROICalculator />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="construction" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.constructionCost')}
                            title="Planificador de Obra"
                            message={getLockedMessage('financial.constructionCost')}
                            primaryCta="Ver Planes"
                            onPrimary={() => navigate('/subscription')}
                        >
                            <div className="max-w-6xl mx-auto">
                                <ConstructionPlanner />
                            </div>
                        </OverlayGate>
                    </TabsContent>

                    <TabsContent value="commission" className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <OverlayGate
                            locked={isLocked('financial.commission')}
                            title="🔒 Herramienta Premium"
                            message={getLockedMessage()}
                            primaryCta="Ver Plan Premium"
                            onPrimary={() => navigate('/subscription')}
                            onSecondary={() => navigate('/subscription')}
                        >
                            <div className="max-w-4xl mx-auto">
                                <CommissionCalculator />
                            </div>
                        </OverlayGate>
                    </TabsContent>
                </Tabs >

                {/* CTA Final */}
                < div className="mt-20 bg-white border border-blue-100 rounded-2xl p-8 md:p-12 text-center shadow-sm relative overflow-hidden" >
                    <div className="relative z-10">
                        <h2 className="text-3xl font-bold text-[#1A3D5C] mb-4">¿Necesitas Asesoría Financiera Personalizada?</h2>
                        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                            Nuestros expertos pueden guiarte para encontrar la mejor opción de financiamiento adaptada a tus necesidades.
                        </p>
                        <Button asChild size="lg" className="bg-[#22C55E] hover:bg-[#1ea850] text-white font-bold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                            <a href="https://wa.me/message/PORUCT4Z63S6E1" target="_blank" rel="noreferrer">
                                Contactar Asesor
                            </a>
                        </Button>
                    </div>
                    {/* Decorative background elements */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-50 to-transparent opacity-50 z-0"></div>
                </div >
            </div >
        </div >
    );
};

export default FinancialCenter;
